import React from "react";
import { useTranslation } from "react-i18next";
import { GitBranch } from "@phosphor-icons/react";
import { labelFor } from "../lib/enumLabels";

/**
 * U9 — Post-calc "Regimes applied" summary chips.
 *
 * The regime is determined per transaction by the classifier (bucket +
 * countries + Settings registrations). This panel shows the breakdown
 * after calculation. Replaces the legacy REGIME dropdown choice.
 */
export const RegimesApplied = ({ regimes, registrations, onFilter }) => {
  // v0.6.12 — operator-reported: this panel showed "EU_LOCAL · 9 TXNS" and
  // "SELLER VAT REGISTRATIONS ACTIVE AT CALC TIME", in typewriter. Three
  // separate faults in one small panel: a raw engine code (EU_LOCAL had no
  // entry in enumLabels' REGIME map at all), invented abbreviations, and the
  // monospace face on prose. The plain-English strings already existed in
  // i18n as ui.regimes_applied.* — written, never wired up.
  const { t } = useTranslation();
  if (!regimes || Object.keys(regimes).length === 0) return null;
  const entries = Object.entries(regimes).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  return (
    <details
      data-testid="regimes-applied-details"
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 group"
    >
      <summary
        data-testid="regimes-applied-summary"
        className="cursor-pointer list-none px-4 py-2 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/60"
      >
        <div className="text-xs tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <GitBranch size={14} weight="bold" />
          {/* v0.5.0i+7 — moved behind a collapsed disclosure per
              operator brief (2026-06-23): this is audit detail,
              not operator-facing routing the staff needs to see
              on first paint. Keep the section's data + UX intact,
              just demote it from default-visible to
              click-to-expand. The chevron caret is the standard
              <details> behaviour — no extra glyph needed. */}
          {t("ui.regimes_applied.title")}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
          {t("ui.regimes_applied.summary_counts", {
            txns: total.toLocaleString(), schemes: entries.length,
          })}
        </div>
      </summary>
      <div className="border-t border-slate-200 dark:border-slate-700 p-4 flex flex-wrap gap-2">
        {entries.map(([name, count]) => {
          // v0.4.37 D4B — palette tightened: emerald/amber/rose/teal
          // only. Sky/violet variants dropped. EU_LOCAL uses a
          // softer teal (info), EU_OSS uses the brighter teal
          // (primary), UK_VAT stays emerald (success).
          const color =
            name === "UK_VAT" ? "bg-emerald-600/10 dark:bg-emerald-900/40 border-emerald-500/40 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300" :
            name === "EU_OSS" ? "bg-teal-50 dark:bg-teal-900/40 border-teal-500/50 dark:border-teal-700 text-teal-700 dark:text-teal-300" :
            name === "EU_LOCAL" ? "bg-teal-50 dark:bg-teal-900/40 border-teal-200 dark:border-teal-800 text-teal-700/80 dark:text-teal-300" :
            "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300";
          // v0.4.33 U6 — operator-confirmed tooltip text.
          const tooltip =
            name === "EU_LOCAL" ? "Same-country sales filed on local national return" :
            name === "EU_OSS" ? "Cross-border B2C filed via Union OSS return" :
            name === "UK_VAT" ? "UK domestic sales" :
            name === "OUT_OF_SCOPE" ? "Marketplace-facilitated, stock movements, exports — not on seller return" :
            (onFilter ? `${labelFor("regime", name)} — click to filter transactions` : labelFor("regime", name));
          const body = (
            <>
              {labelFor("regime", name)} ·{" "}
              {t("ui.regimes_applied.chip_suffix", {
                count: count.toLocaleString(),
              })}
            </>
          );
          // A2-581 lead 19: no screen passes onFilter, yet every chip was a
          // button saying "click to filter" that did nothing. A chip is a
          // button only where clicking it filters.
          const chipClass = `inline-flex items-center px-2 py-1 text-[10px] tracking-normal font-bold border ${color} rounded-full`;
          if (!onFilter) {
            return (
              <span key={name} data-testid={`regime-chip-${name}`} title={tooltip} className={chipClass}>
                {body}
              </span>
            );
          }
          return (
            <button
              key={name}
              type="button"
              data-testid={`regime-chip-${name}`}
              onClick={() => onFilter(name)}
              title={tooltip}
              className={`${chipClass} hover:opacity-90 cursor-pointer`}
            >
              {body}
            </button>
          );
        })}
      </div>
      {registrations && registrations.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400">
          {t("ui.regimes_applied.registrations_label")}:&nbsp;
          <span className="text-slate-500 dark:text-slate-400" data-testid="regimes-registrations">
            {registrations.join(", ")}
          </span>
        </div>
      )}
    </details>
  );
};
